<template>
  <div class="mb-3">
    <h4>Bediener</h4>
    <v-form class="mt-2" v-model="formValid" :ref="form">
      <v-card
        class="bediener-wrapper mb-3"
        color="grey-lighten-4"
        elevation="0"
        v-for="(bed, index) in Bedieners"
        :key="index"
      >
        <v-card-text>
          <v-layout class="bediener-header d-flex align-center mb-2">
            <SelectBediener
              v-model="bed.Bediener"
              :skipped="skippedBedieners"
              outlined
              :class="bed.apiGiven ? undefined : 'white'"
              @input="bed.changed = true"
              :show-label="false"
              :disabled="bed.apiGiven"
              hide-details
            />
            <v-btn
              v-if="Bedieners.length > 1"
              variant="text"
              color="error"
              icon
              style="z-index: 1;"
              @click="deleteBediener(index)"
              class="ml-2"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </v-layout>
          <v-layout d-flex class="overflow-visible">
            <v-text-field
              type="date"
              v-model="bed.WiedervorlageDatum"
              @update:model-value="bed.changed = true"
              variant="outlined"
              class="bg-white"
              hide-details
            />
            <v-text-field
              type="time"
              v-model="bed.WiedervorlageUhrzeit"
              @update:model-value="bed.changed = true"
              label="Uhrzeit"
              hide-details
              variant="outlined"
              class="bg-white ml-2"
            />
          </v-layout>
          <v-layout d-flex>
            <v-switch
              v-model="bed.Gelesen"
              @update:model-value="bed.changed = true"
              label="gelesen?"
              hide-details
              color="primary"
            />
            <v-switch
              v-model="bed.Erledigt"
              @update:model-value="bed.changed = true"
              label="erledigt?"
              hide-details
              class="ml-3"
              color="primary"
            />
          </v-layout>
        </v-card-text>
      </v-card>
      <v-btn
        class="text-primary"
        variant="flat"
        size="small"
        @click="newBediener"
        :disabled="!formValid"
      >
        <v-icon>mdi-plus</v-icon>
        Bediener hinzufügen
      </v-btn>
    </v-form>
  </div>
</template>

<script lang="ts" setup>
import {ApiContactHistoryBedienerSubmitData} from '@/model/ContactHistory';
import SelectBediener from '../SelectBediener.vue';
import {doConfirm} from '@/util';
import {PropType, computed, ref, VNodeRef} from 'vue';
import moment from 'moment';

type ModelData = ApiContactHistoryBedienerSubmitData & {
  changed: boolean;
  apiGiven: boolean;
};

const props = defineProps({
  modelValue: {
    type: Array as PropType<ModelData[]>,
    default: () => [],
  },
});

const emit = defineEmits({
  'update:modelValue': (newData: ModelData[]) => Array.isArray(newData),
});

const Bedieners = computed({
  get: () => props.modelValue,
  set: (newData: ModelData[]) => emit('update:modelValue', newData),
});

const skippedBedieners = computed<Record<string, true>>(() => Object.fromEntries(Bedieners.value.map((bediener) => [bediener.Bediener, true])));

function newBediener() {
  Bedieners.value.push(emptyBediener());
}

async function deleteBediener(index: number) {
  if (
    !(await doConfirm('Diesen Bediener wirklich löschen?', 'Wirklich löschen?'))
  ) {
    return;
  }
  Bedieners.value.splice(index, 1);
}

function emptyBediener() {
  const currentTime = moment();
  const remainder = 15 - (currentTime.minute() % 15);
  return {
    Bediener: '',
    WiedervorlageDatum: moment().format('YYYY-MM-DD'),
    WiedervorlageUhrzeit: moment(currentTime).add(remainder, 'minutes').format('HH:mm'),
    Erledigt: false,
    Gelesen: false,
    changed: true,
    apiGiven: false,
  };
}

const formValid = ref(false);
const form = ref<HTMLFormElement & VNodeRef | undefined>();

defineExpose({
  Bedieners,
  formValid,
  form,
  skippedBedieners,
  newBediener,
  deleteBediener,
  SelectBediener,
});
</script>
<style>
.small-checkbox .v-label {
  font-size: 0.8rem;
}
</style>
